import React, { useEffect, useRef, useMemo } from 'react'
import HydrationSplitter from '../HydrationSplitter.jsx'
import { algoliaClient } from '@/lib/algolia'
import {
  InstantSearch,
  connectInfiniteHits,
  connectStateResults,
  Configure,
  connectHits
} from 'react-instantsearch-dom'
import App from '../App'
import Hero from '../browse/Hero'
import CenterFeed from '../browse/CenterFeed'
import Banner from '../molecules/ContentPicture/ContentPicture'
import SEO from '../browse/SEO'
import CenterCarouselTermHeader from '../molecules/CenterCarousel/CenterCarouselTermHeader.jsx'
import { BrowseFAQs } from '../organisms/BrowseFAQs/BrowseFAQs'
import CentersNearYou from '../organisms/CentersNearYou/CentersNearYou'
import createURL from '../../algolia/createURL'
import onSearchChange from '../../algolia/onSearchChange.js'
import FilterTopBar from './FilterTopbar'
import HighlightedReviewsBlock from '../organisms/HighlightedReviews/HighlightedReviewsBlock'
import Container from '@/components/Container'
import Main from '@/components/Main'
import Section from '@/components/Section'
import SectionHeader from '@/components/SectionHeader'
import SectionHeadingSmall from '@/components/SectionHeadingSmall'
import { MAX_HIT_COUNT, baseStatusFilters } from '@/lib/constants'
import breadcrumbSchema from '../../utils/schema/breadcrumbSchema'
import { FEATURED_CENTER_PLACEMENT } from '@/constants/location'
import useFeaturedCenters from '@/hooks/useFeaturedCenters'
import useFeaturedSUDCenters from '@/hooks/useFeaturedSUDCenters'
import useRenderFeaturedCentersAds from '@/hooks/useRenderFeaturedCentersAds'
import useRenderFeaturedSUDCenters from '@/hooks/useRenderFeaturedSUDCenters'
import CoOccurringCenters from './CoOccurringCenters'
import LocationAndTaxonomyAccordionFAQs from '../organisms/AccordionFAQs/LocationAndTaxonomyAccordionFAQs'
import { sortTerms } from '../../domain/term'
import computeIsLuxury from '../../domain/useCases/term/computeIsLuxury'
import { shouldRenderSpotlightBlock } from '../../domain/useCases/shouldRenderSpotlightBlock'
import { AppContextProvider } from '@/components/AppContextProvider'
import { shouldIndexPage } from '../../domain/useCases/shouldIndexPage'
import { generateCustomTitleForHero } from '../../domain/hero/generateCustomTitleForHero'
import { createDataLayerIfNotExists } from '../../utils/global/createDataLayerIfNotExists.js'
import {
  isLocationPageType,
  isFacetPageType
} from '../../domain/useCases/pageTypeCheck'
import { POSTGRESS_ALGOLIA_MAPPINGS } from '../../lib/constants'
import { getCustomLocationFacetFaqs } from '../../domain/FAQs/getCustomLocationFacetFaqs.js'
import shouldRegionalizeTextForLocation from '../../domain/location/useCases/shouldRegionalizeTextForLocation'
import { isNil } from '../../utils/checks'
import { getLocationRadius } from '../../domain/location/useCases/getLocationRadius'
import centerSchema from '../../utils/schema/centerSchema.ts'
import { getCustomLocationFaqs } from '../../domain/FAQs/getCustomLocationFaqs.js'
import generateBreadcrumbs from '../../domain/useCases/generateBreadcrumbs.ts'
import HeroV2 from './HeroV2.jsx'
import TopLocationsSection from '@/components/browse/TopLocationsSection'
import { getSortedTermsByPageType } from '../../domain/FAQs/getSortedTermsByPageType.js'
import shouldUseNewUI from '@/domain/useCases/shouldUseNewUI'
import PopularTreatmentCarousel from './PopularTreatmentCarousel.jsx'
import { BROWSE_SEARCH_RADIUS_MILES } from '../../domain/search/constants'
import { setSessionStorage } from '../../utils/sessionStorage'
import getDefaultRefinementsByPageType from '../../domain/search/getDefaultRefinementsByPageType'
import { updateRefinementList } from '../../domain/search/updateRefinementList.ts'
import { getLocationPartsTitlesWithoutBannedLocations } from '@/domain/location/useCases/getLocationPartsTitlesWithoutBannedLocations'
import { hasTreatmentTypesToDisplay } from '../../domain/useCases/popularTreatment/hasTreatmentTypesToDisplay.ts'
import CenterCarousel from '../molecules/CenterCarousel/CenterCarousel.tsx'
import { ZoneIds } from '../../domain/zoneIds.ts'
import { getRelatedLinks } from '../../domain/FAQs/getRelatedLinks.ts'
import TopTreatmentLocations from '@/components/organisms/TopTreatmentLocations/TopTreatmentLocations'
export default function BrowseView({
  hitsSource,
  pageType,
  pageContext,
  termData = { title: '', description: '' },
  data,
  serverData,
  /** This is the Gatsby location object */
  location: gatsbyLocation,
  sortedLocations,
  department,
  departments,
  ...rest
}) {
  const contentRef = useRef()
  const showCoOcurringCenters =
    isLocationPageType(pageType) && data.coOccurringDisorders.edges.length >= 5

  const {
    hero_image,
    title,
    header_text,
    content_panels,
    lat,
    lng,
    featuredCenterPlacement,
    reverseTargeting,
    type: locationType
  } = data.location || data.term || termData

  const quick_links = getRelatedLinks(pageType, data)

  const customTermHeader = isFacetPageType(pageType)
    ? termData?.customHeaderText
    : data.term.customTermHeader

  const locationPartsTitles = getLocationPartsTitlesWithoutBannedLocations(
    data.location
  )
  const locationName = locationPartsTitles.join(', ')
  const hits = data.hits
    ? data.hits
    : data[`centers_${pageType}`].edges.map(({ node }) => node)

  const _geoloc = data.location._geoloc || { lat, lng }

  const breadcrumbs = generateBreadcrumbs({
    location: data.location,
    gatsbyLocation,
    term: termData,
    breadcrumbToSkip: title
  })

  let regionalHeaderText = isFacetPageType(pageType)
    ? termData?.header_text
    : header_text

  let shouldDefaultRegionalize = false
  let hasFacetHeader = true

  if (shouldRegionalizeTextForLocation(data.location)) {
    if (termData.header_text) {
      regionalHeaderText = termData.header_text
    } else {
      regionalHeaderText = header_text
      hasFacetHeader = false
    }
    shouldDefaultRegionalize = true
    regionalHeaderText = regionalHeaderText
      ?.replace('Centers', 'Centres')
      ?.replace('Center', 'Centre')
  }

  const refinementList = pageContext.searchState.refinementList || {}
  const parentLocation = pageContext.searchState?.parentLocation || ''
  const related_locations = sortedLocations

  function computeFeaturedCenters(featuredCenters, sponsorCenters) {
    const centers = []
    const shownCenters = new Set()

    if (featuredCenters) {
      featuredCenters.edges.forEach((c) => {
        if (!shownCenters.has(c.node?.title)) {
          shownCenters.add(c.node?.title)
          centers.push(c.node)
        }
      })

      const arr = sponsorCenters.edges
      arr.forEach((c) => {
        if (!shownCenters.has(c.node?.title)) {
          shownCenters.add(c.node.title)
          centers.push(c.node)
        }
      })
    } else {
      sponsorCenters.edges.forEach((c) => {
        if (!shownCenters.has(c.node?.title)) {
          shownCenters.add(c.node.title)
          centers.push(c.node)
        }
      })
    }
    return centers.slice(0, MAX_HIT_COUNT)
  }

  const featuredCenters = useMemo(
    () => computeFeaturedCenters(data.featuredCenters, data.sponsorCenters),
    [data.featuredCenters, data.sponsorCenters]
  )

  useEffect(() => {
    const dataLayerPageTypes = {
      who_we_treat: 'Clientele',
      what_we_treat: 'Condition',
      Location: 'Location',
      price: 'Price',
      amenity: 'Amenity',
      activity: 'Activity',
      setting: 'Setting',
      'treatment-method': 'Treatment Method',
      treatment_services: 'Treatment Services',
      accepted_insurance: 'Insurance'
    }

    // Note: due to React.StrictMode, this useEffect might be called twice in development –
    // which means we’ll see two dataLayer.push calls in the console. This is expected behavior
    // and won’t happen in production.
    createDataLayerIfNotExists()
    window.dataLayer.push({
      algoliaUserToken: 'user-1'
    })
    window.dataLayer.push({ event: 'Hits Viewed' })
    if (!isFacetPageType(pageType) && dataLayerPageTypes[pageType]) {
      window.dataLayer.push({
        pageType: dataLayerPageTypes[pageType]
      })
    } else if (isFacetPageType(pageType)) {
      window.dataLayer.push({
        pageType: pageType
      })
    }
  }, [])

  const jsonSchemaArray = []
  const schemaList = hits.map((center) => center).slice(0, 50)

  if (schemaList.length !== 0) {
    schemaList.forEach((center) => {
      jsonSchemaArray.push(centerSchema({ center, location: data }))
    })
  }

  if (breadcrumbs) {
    jsonSchemaArray.push(
      breadcrumbSchema({
        breadcrumbs,
        pageType,
        termTitle: data?.term?.title,
        termSlug: data?.term?.slug
      })
    )
  }

  const scriptLoop = jsonSchemaArray.map((card) => {
    const stringifiedCard = JSON.stringify(card)
    return (
      <script
        key={stringifiedCard}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: stringifiedCard
        }}
        type="application/ld+json"
      />
    )
  })

  useEffect(() => {
    const itemObjects = []
    const itemList = featuredCenters.map((node) => {
      return node
    })

    if (itemList.length !== 0) {
      createDataLayerIfNotExists()
      itemList.forEach((node, i) => {
        if (!node) return
        let singleObject = {}
        singleObject['item_name'] = node.title
        singleObject['item_id'] = node.slug
        singleObject['item_list_name'] = 'Featured Rehabs'
        singleObject['index'] = i + 1
        singleObject['item_category'] = window.location.href
        itemObjects.push(singleObject)
      })
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          items: itemObjects
        }
      })
    }
  }, [])

  const filterableTaxonomies = data.filterableTaxonomies

  const showFeaturedCenters = featuredCenterPlacement
    ? featuredCenterPlacement === FEATURED_CENTER_PLACEMENT.STANDARD
    : true

  const { isLoading, centers: featuredCentersAds } = useFeaturedCenters({
    termAttr: POSTGRESS_ALGOLIA_MAPPINGS[termData.type] || termData.type,
    termValue: termData.title,
    location: data.location
  })

  const { shouldRenderFeaturedCentersAds } = useRenderFeaturedCentersAds({
    isLoading,
    centers: featuredCentersAds,
    pageType
  })

  const {
    isLoading: isLoadingFeaturedSUDCenters,
    centers: featuredSUDCenters
  } = useFeaturedSUDCenters({
    termAttr: POSTGRESS_ALGOLIA_MAPPINGS[termData.type] || termData.type,
    termValue: termData.title,
    location: data.location,
    pageType,
    searchState: {
      ...refinementList
    }
  })

  const { shouldRenderFeaturedSUDCenters } = useRenderFeaturedSUDCenters({
    isLoading: isLoadingFeaturedSUDCenters,
    centers: featuredSUDCenters,
    pageType,
    showFeaturedCenters
  })

  const MINIMUM_CENTER_THRESHOLD = 3

  function isCenterCountMinimumThreshold(data, pageType) {
    return data[`centers_${pageType}`].edges.length > MINIMUM_CENTER_THRESHOLD
  }

  const isFacetPage = isFacetPageType(pageType)
  const isLocationPage = isLocationPageType(pageType)
  const customFaqs = isLocationPage
    ? getCustomLocationFaqs({
        faqs: data.location?.faqs,
        allLocationsWithParents: data.allLocationsWithParents?.edges,
        locationParts: data.location?.locationParts
      })
    : getCustomLocationFacetFaqs(
        data.location?.faqs || data.location?.parentFaqs,
        data.term?.faqs,
        data.term?.title
      )
  const facetLocation = isFacetPage ? data.location.title : null

  const customFAQs = isLocationPage
    ? customFaqs
        .filter(({ termId }) => isNil(termId))
        .concat(data.term?.faqs || [])
    : customFaqs

  const centerCount =
    data[`centers_${pageType}`]?.edges?.length || data?.hitCount

  useEffect(() => {
    setSessionStorage('searchState', {
      refinementList: updateRefinementList({
        refinementList,
        updates: getDefaultRefinementsByPageType(pageType)
      }),
      configure: {
        aroundLatLng: `${_geoloc.lat},${_geoloc.lng}`
      },
      aroundLatLng: `${_geoloc.lat},${_geoloc.lng}`,
      locationName,
      locationType,
      customLocationRadiusInMiles: getLocationRadius(
        data.location,
        BROWSE_SEARCH_RADIUS_MILES
      ),
      parentLocation
    })
  })

  return (
    <App
      showBreadCrumbs={true}
      hideBreadCrumbs={shouldUseNewUI(pageType)}
      pageType={pageType}
      title={isFacetPageType(pageType) ? termData.title : title}
      breadcrumbs={breadcrumbs}
      shouldDefaultRegionalize={shouldDefaultRegionalize}
      isLuxury={computeIsLuxury(termData)}
      facetLocation={facetLocation}
      location={data.location}
      term={data.term}
      department={department}
      departments={departments}
    >
      <AppContextProvider
        value={{
          breadcrumbs: [...breadcrumbs, title],
          pageType
        }}
      >
        <InstantSearch
          searchState={{
            refinementList: {
              ...refinementList
            }
          }}
          searchClient={algoliaClient}
          indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME}
          onSearchStateChange={(state) => {
            onSearchChange(
              {
                ...state,
                configure: {
                  ...state.configure,
                  aroundLatLng: `${_geoloc.lat},${_geoloc.lng}`
                },
                aroundLatLng: `${_geoloc.lat},${_geoloc.lng}`,
                locationName,
                locationType,
                customLocationRadiusInMiles: getLocationRadius(
                  data.location,
                  BROWSE_SEARCH_RADIUS_MILES
                ),
                parentLocation
              },
              filterableTaxonomies
            )
          }}
          distinct
          facetingAfterDistinct
          createURL={createURL}
        >
          <Configure
            clickAnalytics
            userToken={'user-1'}
            facetingAfterDistinct
            filters={baseStatusFilters.join(' AND ')}
            hitsPerPage={1}
          />
          <ConnectedSEO
            gatsbyCount={hits.length}
            title={title}
            pageType={pageType}
            termData={termData}
            pathParam={rest['*']}
            path={rest.path}
            customTitle={
              isLocationPageType(pageType) ? null : termData.meta_title
            }
            customDescription={
              isLocationPageType(pageType) ? null : termData.meta_description
            }
            imgUrl={hero_image}
            noIndex={
              !shouldIndexPage(data?.term, data?.location, hits, pageType)
            }
            shouldDefaultRegionalize={shouldDefaultRegionalize}
            location={data.location}
          />
          <Main>
            <div className="mb-[-16px] lg:mb-0">
              {shouldUseNewUI(pageType) ? (
                <div className="mb-[-48px] lg:mb-0">
                  <HeroV2
                    overlay={0.9}
                    termData={termData}
                    title={title}
                    pageType={pageType}
                    background={hero_image}
                    count={data.hitCount || hits.length}
                    secondaryTerm={termData.title}
                    customTitle={generateCustomTitleForHero(
                      pageType,
                      title,
                      data?.term?.title,
                      regionalHeaderText,
                      hasFacetHeader
                    )}
                    topLocation={
                      !isFacetPageType(pageType) &&
                      related_locations &&
                      related_locations.length > 3
                    }
                    shouldDefaultRegionalize={shouldDefaultRegionalize}
                    customTermHeader={customTermHeader}
                    breadcrumbs={breadcrumbs}
                    facetLocation={facetLocation}
                    location={data.location}
                    center={data.center}
                    term={data.term}
                  />
                </div>
              ) : (
                <Hero
                  overlay={0.9}
                  termData={termData}
                  title={title}
                  pageType={pageType}
                  background={hero_image}
                  count={data.hitCount || hits.length}
                  secondaryTerm={termData.title}
                  customTitle={generateCustomTitleForHero(
                    pageType,
                    title,
                    data?.term?.title,
                    regionalHeaderText,
                    hasFacetHeader
                  )}
                  topLocation={
                    !isFacetPageType(pageType) &&
                    related_locations &&
                    related_locations.length > 3
                  }
                  shouldDefaultRegionalize={shouldDefaultRegionalize}
                  customTermHeader={customTermHeader}
                  breadcrumbs={breadcrumbs}
                  facetLocation={facetLocation}
                  location={data.location}
                  center={data.center}
                  term={data.term}
                />
              )}
            </div>
            {!isFacetPageType(pageType) &&
              related_locations &&
              related_locations?.length > 3 &&
              !shouldUseNewUI(pageType, data?.location) && (
                <HydrationSplitter>
                  <Section>
                    <Container>
                      <TopLocationsSection
                        relatedLocations={related_locations}
                        pageType={pageType}
                        title={title}
                      />
                    </Container>
                  </Section>
                </HydrationSplitter>
              )}
            {!isFacetPageType(pageType) && !isLocationPageType(pageType) && (
              <HydrationSplitter>
                <CentersNearYou
                  attribute={pageType}
                  condition={data.term.title || ''}
                  review
                  shouldDefaultRegionalize={shouldDefaultRegionalize}
                />
              </HydrationSplitter>
            )}

            {shouldRenderFeaturedCentersAds && (
              <CenterCarouselTermHeader
                termAttr={pageType}
                termValue={termData.title}
                title={title}
                review={true}
                header_text={termData.header_text}
                isLoading={isLoading}
                centers={featuredCentersAds}
                customTermHeader={customTermHeader}
                shouldDefaultRegionalize={shouldDefaultRegionalize}
                zoneId={ZoneIds.Featured}
                containerId="featuredCenters"
              />
            )}

            {shouldRenderFeaturedSUDCenters && (
              <CenterCarousel
                containerId="featuredSUDCenters"
                centers={featuredSUDCenters}
                useFieldsVariable={true}
                sectionHeading="Featured Drug & Alcohol Rehabs"
                isLoading={isLoadingFeaturedSUDCenters}
                shouldDefaultRegionalize={shouldDefaultRegionalize}
                hideDescription={true}
                headerSize="lg"
                headerWeight="medium"
                zoneId={ZoneIds.Featured}
                termTitle={termData?.title}
              />
            )}
            {/* Secondary jumplink id to account for lazy loading */}

            {/* FEED */}
            <HydrationSplitter>
              <Section className={'!mt-10 sm:!mt-14'}>
                <Container>
                  <div
                    data-insights-index={process.env.GATSBY_ALGOLIA_INDEX_NAME}
                  >
                    <ConnectedCenterFeed
                      searchState={{
                        ...refinementList
                      }}
                      reverseTargeting={reverseTargeting}
                      pageData={data}
                      hitCount={data.hitCount}
                      staticHits={hits}
                      hasHero={hero_image || false}
                      loc={{
                        lat: _geoloc ? _geoloc.lat : null,
                        lng: _geoloc ? _geoloc.lng : null
                      }}
                      locationType={locationType}
                      parentLocation={parentLocation}
                      currentRefinements={refinementList}
                      hitsSource={hitsSource || 'algolia'}
                      pageType={pageType}
                      termData={termData}
                      title={title}
                      condition={data.term.title}
                      customTermHeader={customTermHeader}
                      breadcrumbs={breadcrumbs}
                      shouldDefaultRegionalize={shouldDefaultRegionalize}
                      featuredCenters={featuredCenters}
                      customFAQs={customFAQs}
                      sortedLocations={related_locations}
                    >
                      <div className="sticky top-[66px] z-20 m-auto -mx-4 max-w-[1400px] bg-white md:top-[72px] md:mx-0">
                        <FilterTopBar
                          filterableTaxonomies={filterableTaxonomies}
                          contentRef={contentRef}
                          searchState={{
                            ...refinementList
                          }}
                          facetPage
                          includeInsurance={
                            hits[0]?.locations.includes('United States') &&
                            pageType !== 'what_we_treat' &&
                            pageType !== 'who_we_treat' &&
                            pageType !== 'setting' &&
                            pageType !== 'treatment_therapy' &&
                            pageType !== 'special_consideration' &&
                            pageType !== 'amenities' &&
                            pageType !== 'activity' &&
                            pageType !== 'aftercare'
                          }
                          locationAddressParts={locationPartsTitles}
                          department={department}
                        />
                      </div>
                    </ConnectedCenterFeed>
                  </div>
                </Container>
              </Section>
            </HydrationSplitter>
            <div className="mx-auto max-w-screen-xl px-4 md:px-6 lg:px-8">
              <div
                className={`${
                  shouldUseNewUI ? 'lg:w-[calc(100%-440px)]' : 'lg:w-full'
                } flex flex-col gap-10 md:gap-12`}
              >
                {pageType == 'Location' &&
                  title !== 'United States' &&
                  isCenterCountMinimumThreshold(data, pageType) &&
                  hasTreatmentTypesToDisplay(data, pageType) && (
                    <HydrationSplitter>
                      <PopularTreatmentCarousel
                        title={title}
                        locationSlug={data.location.slug}
                        parentLocation={parentLocation}
                        shouldDefaultRegionalize={shouldDefaultRegionalize}
                        shouldDisplay={
                          data[`centers_${pageType}`].edges.length > 12
                        }
                      />
                    </HydrationSplitter>
                  )}

                {shouldRenderSpotlightBlock({
                  pageType,
                  reviewCount: data.pageReviews?.edges?.length
                }) && (
                  <HydrationSplitter>
                    <HighlightedReviewsBlock
                      title={title}
                      primaryFocus={
                        isLocationPageType(pageType)
                          ? 'Substance Use'
                          : data?.term?.primaryFocus
                      }
                      pageType={pageType}
                      filterableTaxonomies={filterableTaxonomies}
                      reviews={data.pageReviews.edges.map(({ node }) => node)}
                    />
                  </HydrationSplitter>
                )}

                {data[`centers_${pageType}`] &&
                  data[`centers_${pageType}`].edges &&
                  content_panels && (
                    <HydrationSplitter>
                      {content_panels.map((panel, index) => {
                        const isIndexEvenNumber = index % 2 === 0
                        if (panel.image && panel.title && panel.content) {
                          return (
                            <Section key={index}>
                              <Banner
                                charcoal={isIndexEvenNumber}
                                video={panel.video || null}
                                title={panel.title}
                                content={panel.content}
                                reverse
                                gray={!isIndexEvenNumber}
                                link={
                                  panel.link
                                    ? {
                                        link: panel.link,
                                        text: 'Learn More'
                                      }
                                    : null
                                }
                                text="View Centers"
                                image={panel.image}
                              />
                            </Section>
                          )
                        }
                        return null
                      })}
                    </HydrationSplitter>
                  )}
                {showCoOcurringCenters && (
                  <HydrationSplitter>
                    <ConnectedCoOccurringCenters
                      pageType={pageType}
                      location={data.location}
                      coOccurringDisorders={data.coOccurringDisorders}
                      hitCount={data.hitCount || hits.length}
                      shouldDefaultRegionalize={shouldDefaultRegionalize}
                    />
                  </HydrationSplitter>
                )}
                {isFacetPageType(pageType) &&
                  related_locations &&
                  related_locations.length > 3 && (
                    <HydrationSplitter>
                      <Section>
                        <TopTreatmentLocations
                          locations={data.relatedLocationsWithCenterCount}
                          pageType={pageType}
                          term={termData}
                        />
                      </Section>
                    </HydrationSplitter>
                  )}

                {(isLocationPage || isFacetPage) &&
                  !shouldUseNewUI(pageType, data.location) && (
                    <HydrationSplitter>
                      <LocationAndTaxonomyAccordionFAQs
                        {...data.location}
                        {...data.term}
                        pageData={data.location || data.term}
                        pageType={pageType}
                        termData={termData}
                        customFAQs={customFAQs}
                        customTermHeader={data.term?.customHeaderText}
                        staticHits={
                          data[`centers_${pageType}`] &&
                          data[`centers_${pageType}`].edges.map(
                            ({ node }) => node
                          )
                        }
                        centerCount={centerCount}
                        terms={{
                          settings: getSortedTermsByPageType(
                            data,
                            pageType,
                            'settings'
                          ),
                          therapies: getSortedTermsByPageType(
                            data,
                            pageType,
                            'therapies'
                          ),
                          locations: getSortedTermsByPageType(
                            data,
                            pageType,
                            'locations'
                          ),
                          options: getSortedTermsByPageType(
                            data,
                            pageType,
                            'options'
                          ),
                          amenities: getSortedTermsByPageType(
                            data,
                            pageType,
                            'onsite_amenities',
                            'offsite_amenities'
                          )
                        }}
                      />
                    </HydrationSplitter>
                  )}
                <div className="my-2 border-b border-gray-300"></div>

                {((isFacetPageType(pageType) && termData.description) ||
                  isLocationPageType(pageType)) && (
                  <HydrationSplitter>
                    <FooterFAQs
                      {...data.location}
                      {...data.term}
                      data={data}
                      customTermHeader={customTermHeader}
                      ref={useRef(data.term.ref)}
                      quick_links={quick_links}
                      pageData={data.location || data.term}
                      pageType={pageType}
                      termData={termData}
                      shouldDefaultRegionalize={shouldDefaultRegionalize}
                      staticHits={
                        data[`centers_${pageType}`] &&
                        data[`centers_${pageType}`].edges.map(
                          ({ node }) => node
                        )
                      }
                      terms={{
                        price:
                          data[`centers_${pageType}`]?.price.sort(sortTerms),
                        settings:
                          data[`centers_${pageType}`]?.settings.sort(sortTerms),
                        therapies:
                          data[`centers_${pageType}`]?.therapies.sort(
                            sortTerms
                          ),
                        locations:
                          data[`centers_${pageType}`]?.locations.sort(
                            sortTerms
                          ),
                        options:
                          data[`centers_${pageType}`]?.options.sort(sortTerms),
                        amenities: [
                          ...(data[`centers_${pageType}`]?.onsite_amenities ??
                            []),
                          ...(data[`centers_${pageType}`]?.offsite_amenities ??
                            [])
                        ].sort(sortTerms)
                      }}
                    />
                  </HydrationSplitter>
                )}
              </div>
            </div>
          </Main>
          {scriptLoop}
        </InstantSearch>
      </AppContextProvider>
    </App>
  )
}

const FooterFAQs = React.forwardRef(BrowseFAQs)
const ConnectedSEO = connectStateResults(SEO)
const ConnectedCenterFeed = connectInfiniteHits(CenterFeed)
const ConnectedCoOccurringCenters = connectHits(CoOccurringCenters)
